import { useEffect, useMemo, useRef, useState } from "react";
import * as api from "../../api/client";
import type { OpenRouterCatalogModel } from "../../api/client";
import { pixelTheme } from "../../theme/pixel";

// Module-level cache: the catalog is large-ish (~600 entries) and
// barely changes between page loads. One fetch per session is plenty.
let cachedCatalog: OpenRouterCatalogModel[] | null = null;
let inflight: Promise<OpenRouterCatalogModel[]> | null = null;

async function loadCatalog(): Promise<OpenRouterCatalogModel[]> {
  if (cachedCatalog) return cachedCatalog;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { models } = await api.listOpenRouterModels();
      cachedCatalog = models;
      return models;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

interface OpenRouterPickerProps {
  /** Currently-selected ids. Each must contain a `/` (provider prefix);
   *  the parent enforces this convention so the wire format stays
   *  uniform with the Claude Code path. */
  selected: string[];
  onChange: (next: string[]) => void;
  /** When `true`, only one chip is allowed; selecting another replaces
   *  the previous one. Used by /admin/generate where the model is a
   *  scalar rather than a list. */
  single?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

/** Autocomplete picker over the OpenRouter model catalog.
 *
 *  Selected models render as chips above the input; click ✕ on a chip
 *  to remove. Typing in the input filters the catalog by id substring
 *  match (case-insensitive); arrow keys navigate the dropdown,
 *  Enter accepts. Click anywhere outside to dismiss the dropdown.
 *
 *  We deliberately don't fuzzy-match across all fields — most
 *  searches are by provider/model id (`opus`, `gemini-2.5`, `gpt-5`),
 *  and id-substring keeps the result list short and predictable.
 */
export function OpenRouterPicker({
  selected,
  onChange,
  single = false,
  disabled,
  placeholder = "search OpenRouter (e.g. gpt-5, opus, gemini)",
}: OpenRouterPickerProps) {
  const theme = pixelTheme;
  const [catalog, setCatalog] = useState<OpenRouterCatalogModel[] | null>(
    cachedCatalog,
  );
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (catalog) return;
    void (async () => {
      try {
        const models = await loadCatalog();
        if (!cancelled) setCatalog(models);
      } catch (err) {
        if (!cancelled) {
          setCatalogError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalog]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const t = window.setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);

  // Filter result: match by case-insensitive substring on id. Drop
  // already-selected ids. Cap to 60 so the dropdown stays scannable
  // (long lists hide the matching item below the fold).
  const matches = useMemo(() => {
    if (!catalog) return [];
    const q = query.trim().toLowerCase();
    const selectedSet = new Set(selected);
    const filtered = catalog
      .filter((m) => !selectedSet.has(m.id))
      .filter((m) => (q === "" ? true : m.id.toLowerCase().includes(q)));
    return filtered.slice(0, 60);
  }, [catalog, query, selected]);

  // Reset highlight when the result set changes — otherwise the user
  // can be left with `activeIdx` past the new list's end.
  useEffect(() => {
    setActiveIdx(0);
  }, [matches.length]);

  function add(id: string) {
    if (!id || !id.includes("/")) return;
    const next = single
      ? [id]
      : selected.includes(id)
        ? selected
        : [...selected, id];
    onChange(next);
    setQuery("");
    setOpen(false);
  }

  function remove(id: string) {
    onChange(selected.filter((s) => s !== id));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(matches.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = matches[activeIdx];
      if (pick) add(pick.id);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && query === "" && selected.length > 0) {
      // Quality-of-life: backspace on an empty input nukes the most
      // recently-added chip. Common pattern in tag inputs.
      remove(selected[selected.length - 1]);
    }
  }

  const inputBase: React.CSSProperties = {
    fontFamily: "monospace",
    fontSize: 13,
    padding: "6px 8px",
    background: theme.bgDeep,
    color: theme.ink,
    border: `2px solid ${theme.panelEdge}`,
    outline: "none",
    minWidth: 240,
    flex: 1,
  };

  return (
    <div ref={wrapRef} style={{ display: "grid", gap: 6, position: "relative" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        {selected.map((id) => (
          <span
            key={id}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 6px",
              fontFamily: "monospace",
              fontSize: 12,
              background: theme.panelSolid,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          >
            {id}
            <button
              type="button"
              onClick={() => remove(id)}
              disabled={disabled}
              aria-label={`remove ${id}`}
              title={`Remove ${id}`}
              style={{
                background: "transparent",
                border: "none",
                color: theme.inkDim,
                cursor: disabled ? "not-allowed" : "pointer",
                padding: 0,
                margin: 0,
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled || (single && selected.length > 0)}
          placeholder={
            single && selected.length > 0
              ? "remove the chip above to pick another"
              : placeholder
          }
          spellCheck={false}
          style={inputBase}
        />
      </div>
      {catalogError && (
        <div style={{ color: theme.accent, fontSize: 12 }}>
          OpenRouter catalog: {catalogError}
        </div>
      )}
      {!catalog && !catalogError && (
        <div style={{ color: theme.inkFaint, fontSize: 12 }}>
          Loading OpenRouter catalog…
        </div>
      )}
      {open && matches.length > 0 && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 30,
            maxHeight: 320,
            overflowY: "auto",
            background: theme.panel,
            border: `2px solid ${theme.panelEdgeStrong}`,
            display: "grid",
            gap: 0,
          }}
        >
          {matches.map((m, i) => {
            const active = i === activeIdx;
            return (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={active}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => add(m.id)}
                style={{
                  display: "grid",
                  gap: 2,
                  padding: "6px 8px",
                  textAlign: "left",
                  background: active ? theme.bgDeep : "transparent",
                  color: theme.ink,
                  border: "none",
                  borderBottom: `1px solid ${theme.panelEdge}`,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <span style={{ fontFamily: "monospace", fontSize: 13 }}>
                  {m.id}
                </span>
                {m.name && m.name !== m.id && (
                  <span style={{ fontSize: 11, color: theme.inkDim }}>
                    {m.name}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {open && matches.length === 0 && catalog && query.trim() !== "" && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 30,
            padding: 8,
            background: theme.panel,
            border: `2px solid ${theme.panelEdge}`,
            color: theme.inkFaint,
            fontSize: 12,
          }}
        >
          No OpenRouter models match "{query}".
        </div>
      )}
    </div>
  );
}
